import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {BehaviorSubject, finalize, Observable, of} from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {
    ManagementAttributeGroupsFormComponent
} from '@emm/management/components/management-attribute-groups/management-attribute-groups-form/management-attribute-groups-form.component';
import * as RA from 'ramda-adjunct';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {AttributeGroupsFacadeService} from '@emm/management/services/attribute-groups-facade.service';
import * as R from 'ramda';
import {MatDialog} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {DialogService} from '@emm/shared/services/dialog.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {attributeGroupsFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/attribute-groups-filter.config';
import {
    AddAttributeGroupDialogComponent
} from '@emm/shared/dialogs/add-attribute-group-dialog/add-attribute-group-dialog.component';
import {SearchFilter} from '@emm/core/models/search-filter';
import {
    DeleteAttributeGroupDialogComponent
} from '@emm/shared/dialogs/delete-attribute-group-dialog/delete-attribute-group-dialog.component';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from "@emm/core/util/api-constants";


@Component({
    selector: 'emm-management-attribute-groups',
    templateUrl: './management-attribute-groups.component.html',
    styleUrls: ['./management-attribute-groups.component.scss']
})
export class ManagementAttributeGroupsComponent implements OnInit {

    @ViewChild(ManagementAttributeGroupsFormComponent) attributeGroupsForm: ManagementAttributeGroupsFormComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    attributeGroups$ = new BehaviorSubject<any>(undefined);
    attributeGroupDetail$ = this.attributeGroupsFacade.attributeGroupDetail$;
    fields = [fieldMap['id'](), fieldMap['name'](), fieldMap['description']()];
    centerLoading$ = this.attributeGroupsFacade.centerLoading$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    rightLoading$ = this.attributeGroupsFacade.rightLoading$;
    anyLoading$ = this.attributeGroupsFacade.anyLoading$;
    attributeGroupsCache = [];
    prevAttributeGroup: string;
    attributeGroupsContextMenuActions = [
        {
            enabled: () => true,
            click: (attributeGroup) => this.openDeleteAttributeGroupDialog(attributeGroup),
            visible: this.isSuperAdmin ? R.T : R.F,
            icon: R.always('delete'),
            text: R.always('Delete'),
            tooltip: () => 'Delete an Attribute Group'
        },
    ] as ContextMenuAction<AttributeGroup>[];

    attributeGroupsAsGenericItems$ = this.attributeGroupsFacade.attributeGroupsAsGenericItems$;

    constructor(
        private attributeGroupsFacade: AttributeGroupsFacadeService,
        private dialogService: DialogService,
        private dialog: MatDialog,
        private managementApiService: ManagementApiService,
        private notificationService: NotificationService,
        private authFacade: AuthFacadeService,
        private userService: UserService,
    ) {}


    ngOnInit(): void {
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getAttributeGroups();
    }


    getAttributeGroups() {
        this.attributeGroupsFacade.startProgressBar();
        this.attributeGroupsAsGenericItems$
            .pipe(
                finalize(() => this.attributeGroupsFacade.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            this.attributeGroupsCache = result;
            this.attributeGroups$.next(this.attributeGroupsCache);
        });
    }

    openCreateAttributeGroupDialog(): void {
        const dialogRef = this.dialog.open(AddAttributeGroupDialogComponent, {
            width: '600px',
            data: {}
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((attributeGroup) => attributeGroup),
                switchMap((attributeGroup) => this.attributeGroupsFacade.api.createAttributeGroup(attributeGroup)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.centerLoading$ = of(true);
                    this.notificationService.success('Attribute Group Created');
                    this.getAttributeGroups();
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    onFilter(groupFilter: SearchFilter): void {
        this.filterNodes(groupFilter.value as string, groupFilter.key)
    }


    filterNodes(value: string, selected: string): void {
        this.attributeGroups$.next(this.attributeGroupsCache);
        if (!value) {
            return;
        }
        this.attributeGroups$.pipe(
            take(1),
            map(list => {
                const filteredNodes = list.filter(nd => nd['source'][selected].toLowerCase().includes(value.toLowerCase()));
                this.attributeGroups$.next(filteredNodes);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }


    onSelectAttributeGroup(selected: AttributeGroup): void {
        if (!RA.isNotNil(selected)) {
            this.prevAttributeGroup = null;
            return;
        }
        this.checkIfAttributeGroupHasChanges(this.prevAttributeGroup)
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((_) => {
                this.prevAttributeGroup = selected['source'].id;
                this.attributeGroupsFacade.selectAttributeGroup(selected['source'].id);
            });
    }

    checkIfAttributeGroupHasChanges(prevAttributeGroup: string): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevAttributeGroup)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevAttributeGroup)
                        .afterClosed()
                        .pipe(
                            switchMap(res => {
                                if (res) {
                                    this.attributeGroupsForm.submit();
                                    return this.attributeGroupsFacade.updatedAttributeGroup$ as Observable<boolean>;
                                }
                                this.attributeGroupsForm.cancel();
                                return of(true);
                            })
                        );
                }
                return of(true);
            })
        );
    }


    hasChanges(): boolean {
        return this.attributeGroupsForm && this.attributeGroupsForm.hasChanges();
    }


    openDeleteAttributeGroupDialog(attributeGroup) {
        const dialogRef = this.dialog.open(DeleteAttributeGroupDialogComponent, {
            width: '600px',
            data: {
                categoryData: {
                    id: attributeGroup.id,
                    name: attributeGroup.name,
                    description: attributeGroup.description
                }
            }
        });
        dialogRef
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                if (!result) {
                    // User clicked cancel
                    this.notificationService.error('Cancelled Delete Attribute Group');
                    return;
                }
                this.managementApiService.deleteAttributeGroup(result.id)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef)
                    )
                    .subscribe({
                        next: () => {
                            this.notificationService.success(`Successfully deleted ${attributeGroup.id}`);
                            this.getAttributeGroups();
                        },
                        error: (error) => {
                            this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                        }
                    });
            });
    }
}
